import React from "react";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import vgToast from "utils/toast";
import {
  BookingConfig,
  createBookingConfig,
  readBookingConfigs,
  updateBookingConfig,
} from "services/booking-configs.service";
import { ACTIVATION_STATE } from "constants/bookingEnums";
import BookingConfigDialog, {
  BookingConfigFormValues,
} from "./BookingConfigDialog";

type Row = {
  id: string;
  name: string;
  status: string;
  resources: string;
  perDay: string;
  price: string;
};

function toRow(c: BookingConfig): Row {
  return {
    id: String(c.serviceFulfilmentConfigId),
    name: c.name || "(unnamed)",
    status: c.isActive === ACTIVATION_STATE.ACTIVE ? "Active" : "Inactive",
    resources: String(c.numberOfRequiredResources ?? 1),
    // 0 means no limit in the contract, which reads as a bug if shown raw.
    perDay: c.maxOrdersPerDay ? String(c.maxOrdersPerDay) : "No limit",
    price: c.monetaryAmount ? String(c.monetaryAmount) : "—",
  };
}

type Props = {
  /** Driven by the "Add Booking Configuration" button in the detail header. */
  createOpen: boolean;
  onCreateClose: () => void;
};

const BookingConfigsList = ({ createOpen, onCreateClose }: Props) => {
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const serviceId = id ? Number(id) : NaN;
  const serviceIdValid = Number.isFinite(serviceId) && serviceId > 0;

  const [configs, setConfigs] = React.useState<BookingConfig[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<BookingConfig | null>(null);
  const [saving, setSaving] = React.useState(false);

  const rows = React.useMemo(() => configs.map(toRow), [configs]);
  const view = useTableView(rows);

  const load = React.useCallback(async () => {
    if (!isAuthenticated || orgId <= 0) {
      setConfigs([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await readBookingConfigs(token, orgId);
      if (apiError) {
        setError(apiError.message);
        setConfigs([]);
        return;
      }
      // The endpoint is org-scoped, so narrow to this service ourselves.
      setConfigs(
        (data ?? []).filter((c) => !serviceIdValid || c.serviceId === serviceId)
      );
    } catch (e) {
      setError((e as Error).message);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, orgId, serviceId, serviceIdValid]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (values: BookingConfigFormValues) => {
    if (orgId <= 0 || !serviceIdValid) {
      vgToast.error("Choose an active provider and a service first.");
      return;
    }
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const payload = { ...values, orgId, serviceId };
      const { error: apiError } = editing
        ? await updateBookingConfig(token, {
            ...payload,
            serviceFulfilmentConfigId: editing.serviceFulfilmentConfigId,
          })
        : await createBookingConfig(token, payload);

      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      vgToast.success(
        editing ? "Booking configuration saved." : "Booking configuration added."
      );
      setEditing(null);
      onCreateClose();
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const dialogOpen = createOpen || editing !== null;

  return (
    <div>
      <PageSizeSelect
        value={view.pageSize}
        onChange={view.setPageSize}
        className="m-4"
      />

      <Table<Row>
        loading={loading}
        columns={{
          name: "Name",
          status: "Status",
          resources: "Resources",
          perDay: "Per Day",
          price: "Price",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
        emptyMessage="No booking configurations for this service yet. Add one to make it bookable."
        handleViewItem={(rowId) => {
          const found = configs.find(
            (c) => String(c.serviceFulfilmentConfigId) === rowId
          );
          if (found) setEditing(found);
        }}
      />

      {loading && (
        <div className="px-4 pt-2 text-sm text-slate-500">
          Loading booking configurations…
        </div>
      )}
      {error && <div className="px-4 pt-2 text-sm text-red-500">{error}</div>}

      <PaginationBar
        from={view.from}
        to={view.to}
        total={view.total}
        currentPage={view.page}
        totalPages={view.totalPages}
        onPrev={view.goPrev}
        onNext={view.goNext}
        onPageSelect={view.goToPage}
      />

      <BookingConfigDialog
        open={dialogOpen}
        existing={editing}
        busy={saving}
        onCancel={() => {
          if (saving) return;
          setEditing(null);
          onCreateClose();
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default BookingConfigsList;
